/**
 * Enhanced Control Tray with improved UX
 */

import cn from "classnames";
import { memo, ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { UseMediaStreamResult } from "../../hooks/use-media-stream-mux";
import { useScreenCapture } from "../../hooks/use-screen-capture";
import { useWebcam } from "../../hooks/use-webcam";
import { AudioRecorder } from "../../lib/audio-recorder";
import AudioPulse from "../audio-pulse/AudioPulse";
import { Button } from "../ui/Button";
import "./enhanced-control-tray.scss";

export type EnhancedControlTrayProps = {
  videoRef: RefObject<HTMLVideoElement>;
  children?: ReactNode;
  supportsVideo: boolean;
  onVideoStreamChange?: (stream: MediaStream | null) => void;
  enableEditingSettings?: boolean;
};

type MediaStreamButtonProps = {
  isStreaming: boolean;
  onIcon: string;
  offIcon: string;
  start: () => Promise<any>;
  stop: () => any;
  label: string;
  tooltip: string;
};

/**
 * Enhanced button for media stream controls with tooltips
 */
const MediaStreamButton = memo(
  ({
    isStreaming,
    onIcon,
    offIcon,
    start,
    stop,
    label,
    tooltip,
  }: MediaStreamButtonProps) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
      <div className="media-stream-button-container">
        <Button
          variant={isStreaming ? "danger" : "secondary"}
          size="md"
          onClick={isStreaming ? stop : start}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          aria-label={`${isStreaming ? "Stop" : "Start"} ${label}`}
          leftIcon={
            <span className="material-symbols-outlined">
              {isStreaming ? onIcon : offIcon}
            </span>
          }
        >
          {isStreaming ? "Stop" : "Start"}
        </Button>
        {showTooltip && (
          <div className="tooltip" role="tooltip">
            {tooltip}
          </div>
        )}
      </div>
    );
  }
);

export function EnhancedControlTray({
  videoRef,
  children,
  onVideoStreamChange = () => {},
  supportsVideo,
  enableEditingSettings,
}: EnhancedControlTrayProps) {
  const videoStreams = [useWebcam(), useScreenCapture()];
  const [activeVideoStream, setActiveVideoStream] =
    useState<MediaStream | null>(null);
  const [webcam, screenCapture] = videoStreams;
  const [inVolume, setInVolume] = useState(0);
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [muted, setMuted] = useState(false);
  const [showMicTooltip, setShowMicTooltip] = useState(false);
  const [showConnectTooltip, setShowConnectTooltip] = useState(false);
  const renderCanvasRef = useRef<HTMLCanvasElement>(null);
  const connectButtonRef = useRef<HTMLButtonElement>(null);

  const { client, connected, connect, disconnect, volume } =
    useLiveAPIContext();

  useEffect(() => {
    if (!connected && connectButtonRef.current) {
      connectButtonRef.current.focus();
    }
  }, [connected]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--volume",
      `${Math.max(5, Math.min(inVolume * 200, 8))}px`
    );
  }, [inVolume]);

  useEffect(() => {
    const onData = (base64: string) => {
      client.sendRealtimeInput([
        {
          mimeType: "audio/pcm;rate=16000",
          data: base64,
        },
      ]);
    };
    if (connected && !muted && audioRecorder) {
      audioRecorder.on("data", onData).on("volume", setInVolume).start();
    } else {
      audioRecorder.stop();
    }
    return () => {
      audioRecorder.off("data", onData).off("volume", setInVolume);
    };
  }, [connected, client, muted, audioRecorder]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = activeVideoStream;
    }

    let timeoutId = -1;

    function sendVideoFrame() {
      const video = videoRef.current;
      const canvas = renderCanvasRef.current;

      if (!video || !canvas) {
        return;
      }

      const ctx = canvas.getContext("2d")!;
      canvas.width = video.videoWidth * 0.25;
      canvas.height = video.videoHeight * 0.25;
      if (canvas.width + canvas.height > 0) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const base64 = canvas.toDataURL("image/jpeg", 1.0);
        const data = base64.slice(base64.indexOf(",") + 1, Infinity);
        client.sendRealtimeInput([{ mimeType: "image/jpeg", data }]);
      }
      if (connected) {
        timeoutId = window.setTimeout(sendVideoFrame, 1000 / 0.5);
      }
    }
    if (connected && activeVideoStream !== null) {
      requestAnimationFrame(sendVideoFrame);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [connected, activeVideoStream, client, videoRef]);

  const changeStreams = (next?: UseMediaStreamResult) => async () => {
    if (next) {
      const mediaStream = await next.start();
      setActiveVideoStream(mediaStream);
      onVideoStreamChange(mediaStream);
    } else {
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }

    videoStreams.filter((msr) => msr !== next).forEach((msr) => msr.stop());
  };

  return (
    <section className="enhanced-control-tray">
      <canvas style={{ display: "none" }} ref={renderCanvasRef} />

      {/* Connection Status Indicator */}
      <div className="connection-status">
        <div className={cn("status-indicator", { connected })}>
          <span className="status-dot" />
          <span className="status-text">
            {connected ? "Connected" : "Disconnected"}
          </span>
        </div>
      </div>

      {/* Main Controls */}
      <nav className={cn("enhanced-actions-nav", { disabled: !connected })}>
        {/* Connection Button */}
        <div className="control-group">
          <div className="connection-button-container">
            <Button
              ref={connectButtonRef}
              variant={connected ? "danger" : "primary"}
              size="lg"
              onClick={connected ? disconnect : connect}
              onMouseEnter={() => setShowConnectTooltip(true)}
              onMouseLeave={() => setShowConnectTooltip(false)}
              onFocus={() => setShowConnectTooltip(true)}
              onBlur={() => setShowConnectTooltip(false)}
              aria-label={
                connected ? "Disconnect from Live API" : "Connect to Live API"
              }
              leftIcon={
                <span className="material-symbols-outlined">
                  {connected ? "stop" : "play_arrow"}
                </span>
              }
            >
              {connected ? "Disconnect" : "Connect"}
            </Button>
            {showConnectTooltip && (
              <div className="tooltip" role="tooltip">
                {connected ? "Disconnect from Live API" : "Connect to Live API"}
              </div>
            )}
          </div>
        </div>

        {/* Audio Controls */}
        <div className="control-group">
          <div className="mic-button-container">
            <Button
              variant={muted ? "secondary" : "danger"}
              size="md"
              onClick={() => setMuted(!muted)}
              onMouseEnter={() => setShowMicTooltip(true)}
              onMouseLeave={() => setShowMicTooltip(false)}
              onFocus={() => setShowMicTooltip(true)}
              onBlur={() => setShowMicTooltip(false)}
              aria-label={muted ? "Unmute microphone" : "Mute microphone"}
              className="mic-button"
              leftIcon={
                <span className="material-symbols-outlined filled">
                  {!muted ? "mic" : "mic_off"}
                </span>
              }
            >
              {muted ? "Muted" : "Unmuted"}
            </Button>
            {showMicTooltip && (
              <div className="tooltip" role="tooltip">
                {muted ? "Unmute microphone" : "Mute microphone"}
              </div>
            )}
          </div>

          {/* Audio Visualizer */}
          <div className="audio-visualizer">
            <AudioPulse volume={volume} active={connected} hover={false} />
            <span className="visualizer-label">Audio Level</span>
          </div>
        </div>

        {/* Video Controls */}
        {supportsVideo && (
          <div className="control-group">
            <MediaStreamButton
              isStreaming={screenCapture.isStreaming}
              start={changeStreams(screenCapture)}
              stop={changeStreams()}
              onIcon="cancel_presentation"
              offIcon="present_to_all"
              label="Screen Share"
              tooltip="Share your screen with the AI"
            />
            <MediaStreamButton
              isStreaming={webcam.isStreaming}
              start={changeStreams(webcam)}
              stop={changeStreams()}
              onIcon="videocam_off"
              offIcon="videocam"
              label="Camera"
              tooltip="Share your camera with the AI"
            />
          </div>
        )}

        {/* Custom Controls */}
        {children && (
          <div className="control-group custom-controls">{children}</div>
        )}
      </nav>
    </section>
  );
}
